"use client"

import { router, usePage } from "@inertiajs/react"
import { Eye } from "@untitledui/icons"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"

/*
 * "Browsing as …": shown while a super admin's session has been swapped for
 * another user's, on every page, so nobody forgets whose eyes they are using.
 * A pill at the foot of the viewport, clear of the rail and the page head.
 * Caution grammar: a state that needs a person to end it, not an error.
 * The clock is the server's deadline, so its ending is never a surprise.
 */
export function ImpersonationBanner() {
  const { impersonation } = usePage().props
  const remaining = useRemaining(impersonation?.expires_at)
  if (!impersonation) return null

  return (
    <div
      role="status"
      className="caution-band text-caution-700 fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full px-4 py-2 text-sm shadow-lg"
    >
      <Eye aria-hidden="true" className="size-4 shrink-0" />
      <p className="font-medium">
        Browsing as {impersonation.user_name}
        {remaining && <span className="font-normal"> · {remaining}</span>}
      </p>
      <Button
        size="sm"
        color="secondary"
        onClick={() => router.delete(impersonation.stop_path)}
      >
        Stop
      </Button>
    </div>
  )
}

/** "42 min left", refreshed each half minute; "ending now" once it has passed. */
function useRemaining(expiresAt: string | undefined) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!expiresAt) return
    const timer = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [expiresAt])
  if (!expiresAt) return null

  const minutes = Math.ceil((Date.parse(expiresAt) - now) / 60_000)
  if (minutes <= 0) return "ending now"
  if (minutes < 60) return `${minutes} min left`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours} h ${rest} min left` : `${hours} h left`
}
